import { ImageWithFallback } from '@/components/ui/ImageWithFallback';
import { useDarkMode } from '@/hooks/useDarkMode';
import { cn } from '@/utils/utils';
import { MediaDto } from '@shelflix/trpc/routes/media/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card } from '@shelflix/ui-web/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shelflix/ui-web/components/ui/dialog';
import { Skeleton } from '@shelflix/ui-web/components/ui/skeleton';
import { Clock, Edit2, Play } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface MediaCardProps extends React.HTMLAttributes<typeof Card> {
  media: MediaDto;
  brandName: string;
  onPlay?: () => void;
  onInfo?: () => void;
  showExpiration?: boolean;
}

export function MediaCardVertical({ media, onPlay, onInfo, brandName }: MediaCardProps) {
  const [imageLoaded] = useState(true);
  const [, setVideoError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(media.url);
  const { isDarkMode } = useDarkMode();

  const thumbnailSrc = media.thumbnailUrl;

  const handleVideoError = () => {
    setImageSrc(thumbnailSrc || 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png');
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPlay?.();
  };

  const handleInfoClick = () => {
    if (onInfo) {
      onInfo();
      return;
    }
    navigate(`${media.uuid}`, {
      state: {
        brandName,
      },
    });
  };

  const VideoPreview = () => (
    <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
      {!imageLoaded ? (
        <Skeleton className="h-full w-full" />
      ) : (
        <>
          {['mp4', 'mov', 'webm'].includes(media?.extension ?? '') ? (
            <>
              <video
                ref={videoRef}
                src={imageSrc}
                className="h-full w-full object-cover"
                poster={thumbnailSrc || 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png'}
                onError={handleVideoError}
                onLoadStart={() => setVideoError(false)}
                onCanPlay={() => {
                  setVideoError(false);
                  setIsVideoLoaded(true);
                }}
                muted
                loop
                playsInline
              />
              {isModalOpen && (
                <div className="absolute top-0 left-0 z-30 flex h-full w-full flex-1 items-center justify-center">
                  <Button onClick={handlePlayClick} className="rounded-full bg-white/50 p-5 text-white">
                    <Play className="h-10 w-10" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <img
              className="h-full w-full object-cover"
              alt={media.name + ' thumbnail'}
              src={imageSrc}
              onError={() => {
                setImageSrc('https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png');
              }}
            />
          )}

          {/* Overlay com botões */}
          <div className="absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="flex h-full items-center justify-center gap-2">
              {/* <Button
                size="sm"
                onClick={handlePlayClick}
                className="flex items-center gap-1  /90 text-black hover: "
              >
                <Play className="h-4 w-4" />
                Reproduzir
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleInfoClick}
                className="flex items-center gap-1 bg-black/70 text-white hover:bg-black/90"
              >
                <Info className="h-4 w-4" />
                Info
              </Button> */}
              <Icon name="play" />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const CampaignInfo = () => (
    <div className="p-4">
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-white">{media.name}</h3>
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-300">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{media.duration}s</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {brandName && (
          <Badge variant="outline" className="text-xs">
            {brandName}
          </Badge>
        )}
      </div>
      <div className="mt-3 flex flex-1 justify-center gap-2">
        <Button variant="secondary" size="lg" className="mx-auto" onClick={handleInfoClick}>
          <Edit2 size={28} />
          Editar mídia
        </Button>
      </div>
    </div>
  );

  const CardContent = () => (
    <div
      className={cn(
        'campaign-card-shadow relative w-full overflow-hidden rounded-md',
        isDarkMode ? 'bg-gray-900/50' : 'bg-black'
      )}
      onClick={handleInfoClick}
    >
      <ImageWithFallback
        src={thumbnailSrc}
        alt={media.name + ' thumbnail'}
        aspectRatio="aspect-[9/16]"
        objectFit="contain"
        containerClassName="w-full"
        skeletonClassName="bg-gray-700/50"
      />

      {/* Informações básicas no card */}
      <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black to-transparent p-3">
        <h3 className="truncate text-sm font-medium text-white">{media.name}</h3>
        <div className="mt-1 flex items-center gap-2 text-xs text-gray-300">
          <span>{media.duration}s</span>
          {brandName && <span>• {brandName}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div onClick={handleInfoClick}>
        <CardContent />
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl border-gray-800 bg-black/95 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{media.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <VideoPreview />
            <CampaignInfo />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
